import cookies from 'browser-cookies';
import { A, G } from '@mobily/ts-belt';
import { VISION_ACCEPT_INVITE_PAGE_PATH, VISION_LANDING_PAGE_PATH, VISION_REFERRAL_PAGE_PATH, VISION_MINIMUM_RATED_OUTFITS, VISION_INTENT_COOKIE_NAME, VISION_INTENT_COOKIE_MAX_AGE_DAYS, VISION_INTENT_COOKIE_VALUE } from './constants';
import type { VisionBusinessLine, VisionOutfitOption } from './types';
import type { GetVisionClientQuery } from '../../experiments/visionGiveGet/getVisionClient';
import { GetStyleDiscoveryRatingsQuery } from './graphql/getStyleDiscoveryRatings';
import type { StyleDiscoveryOutfit, StyleDiscoveryOutfitRating } from '../../types/definitions/vendor/graphql-schema';
import { isNonEmptyString } from '../../utils/helpers';
export const setVisionIntentCookie = () => {
  cookies.set(VISION_INTENT_COOKIE_NAME, VISION_INTENT_COOKIE_VALUE, {
    domain: '.stitchfix.com',
    expires: VISION_INTENT_COOKIE_MAX_AGE_DAYS,
    path: '/',
    secure: true
  });
};
export const clearVisionIntentCookie = () => {
  cookies.set(VISION_INTENT_COOKIE_NAME, '', {
    domain: '.stitchfix.com',
    expires: -1,
    path: '/',
    secure: true
  });
};
export const getHasVisionIntentCookie = (): boolean => {
  return cookies.get(VISION_INTENT_COOKIE_NAME) === VISION_INTENT_COOKIE_VALUE;
};
const getReferrerPathname = (referrerURL: string) => {
  try {
    const docReferrer = new URL(referrerURL, 'https://stitchfix.com');
    return docReferrer.pathname;
  } catch (e: unknown) {
    // Catch the error thrown if URL creation is not valid
    return null;
  }
};
export const isVisionLandingPageReferrer = ({
  referrerURL
}: {
  referrerURL: string;
}) => getReferrerPathname(referrerURL) === VISION_LANDING_PAGE_PATH;
export const isSharedVisionReferrer = ({
  referrerURL
}: {
  referrerURL: string;
}) => {
  const referrerPathname = getReferrerPathname(referrerURL);
  if (!referrerPathname) {
    return false;
  }
  return referrerPathname === VISION_REFERRAL_PAGE_PATH || referrerPathname.startsWith(`${VISION_REFERRAL_PAGE_PATH}/`) || referrerPathname === VISION_ACCEPT_INVITE_PAGE_PATH || referrerPathname.startsWith(`${VISION_ACCEPT_INVITE_PAGE_PATH}/`);
};
export const isVisionBusinessLine = (businessLine: unknown): businessLine is VisionBusinessLine => businessLine === 'WOMENS' || businessLine === 'MENS';
export const toVisionBusinessLine = (businessLine: unknown): VisionBusinessLine | null => {
  if (typeof businessLine !== 'string') {
    return null;
  }
  const normalizedBusinessLine = businessLine.toUpperCase();
  return isVisionBusinessLine(normalizedBusinessLine) ? normalizedBusinessLine : null;
};
type OutfitRating = Pick<StyleDiscoveryOutfitRating, 'id' | 'rating' | 'ratedAt'> & {
  __typename: 'StyleDiscoveryOutfitRating';
};
const isOutfitRating = (node: unknown): node is OutfitRating => G.isObject(node) &&
// eslint-disable-next-line no-underscore-dangle
node.__typename === 'StyleDiscoveryOutfitRating';
export const getLikedOutfits = (styleDiscoveryRatings: GetVisionClientQuery['styleDiscoveryRatings'] | GetStyleDiscoveryRatingsQuery['styleDiscoveryRatings'] | undefined) => {
  // Keep only outfit ratings
  const ratings = (styleDiscoveryRatings?.nodes ?? []).filter(node => isOutfitRating(node));

  // Find the latest ratedAt timestamp.
  // Give/get ratings are submitted in bulk, so they will share the same timestamp.
  // We want to ensure we are only looking at the most recent ratings, in case there
  // are multiple sets of ratings (e.g., from different sessions or a SUP client
  // returning after a seasonal refresh).
  const latestTimestamp = A.reduce(ratings, 0, (latest, r) => Math.max(latest, new Date(r.ratedAt).getTime()));

  // Filter and return latest ratings that are true (liked), and only last n ratings
  // if count is present
  return A.filter(ratings, r => new Date(r.ratedAt).getTime() === latestTimestamp && r.rating === true);
};
type VisionOutfitRating = OutfitRating & {
  ratedOutfit: StyleDiscoveryOutfit & {
    concatenatedItemsImageUrl: string;
  };
};
const isVisionOutfitRating = (rating: OutfitRating): rating is VisionOutfitRating => 'ratedOutfit' in rating && G.isObject(rating.ratedOutfit) && 'concatenatedItemsImageUrl' in rating.ratedOutfit && isNonEmptyString(rating.ratedOutfit.concatenatedItemsImageUrl);

/**
 * Parse style discovery ratings for vision queue submit request
 */
export const parseVisionOutfitData = (styleDiscoveryRatings: GetStyleDiscoveryRatingsQuery['styleDiscoveryRatings'] | undefined): VisionOutfitOption[] => {
  const likedOutfits = getLikedOutfits(styleDiscoveryRatings).filter(r => isVisionOutfitRating(r)).slice(0, VISION_MINIMUM_RATED_OUTFITS);
  if (likedOutfits.length !== VISION_MINIMUM_RATED_OUTFITS) {
    throw new Error(`Incorrect number of ratings. Expected: ${VISION_MINIMUM_RATED_OUTFITS}, Got: ${likedOutfits.length}`);
  }
  return likedOutfits.map(r => ({
    id: r.id,
    uniqueId: r.id,
    displayImageUrl: r.ratedOutfit.image.url,
    falGarmentImageUrl: r.ratedOutfit.concatenatedItemsImageUrl,
    garmentImageUrls: r.ratedOutfit.items.map(item => item.image.url),
    outfitSkus: (r.ratedOutfit.outfitId.match(/\d+/g) ?? []).map(id => ({
      skuId: Number(id),
      imageUrl: '' // this is not used but is here to satisfy the vision queue submit schema
    }))
  }));
};
export const getVisionOutfitSkuItemsById = ({
  outfitId,
  visionOutfitOptions
}: {
  outfitId: string;
  visionOutfitOptions: VisionOutfitOption[];
}): {
  skuId: number;
  imageUrl: string;
}[] => {
  const option = visionOutfitOptions.find(o => o.id === outfitId);
  const imageUrls = option?.garmentImageUrls ?? [];
  return imageUrls.map((url, index) => ({
    skuId: index + 1,
    // this is only used for the key when rendering the vision gallery outfits, so we can use the index as the skuId for now
    imageUrl: url
  }));
};
export const getVisionOutfitSkuIdsById = ({
  outfitId,
  visionOutfitOptions
}: {
  outfitId: string;
  visionOutfitOptions: VisionOutfitOption[];
}): number[] => {
  const option = visionOutfitOptions.find(o => o.id === outfitId);
  return option?.outfitSkus.map(s => s.skuId) ?? [];
};